import { Card, Flex, SimpleTable, Typography, Pagination } from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../components/ExpandableCard";

const { Title, Text } = Typography;

const PaginationPage = () => {
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const total = 100;
  const siblingCount = 1;

  const handlePageChange = (offset: number) => {
    setOffset(offset);
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Pagination</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Pagination component in the Djuno Design system provides a
            highly customizable interface for paginating content. It supports
            multiple visual variants, sibling page control, and a loading state,
            making it ideal for navigating large sets of data.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Pagination component offers several customization options to fit
            various use cases:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm" strong>
                Sibling Count
              </Text>

              <Text size="sm">
                : Customize the number of sibling pages displayed around the
                current page. This is controlled by the siblingCount prop, which
                defaults to 1, but can be adjusted based on your design needs.{" "}
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Limit and Offset
              </Text>
              <Text size="sm">
                : Control the number of items per page using the limit prop and
                set the current page’s starting index with the offset prop.
                These allow fine-tuning of the pagination behavior based on the
                size of data sets.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Loading State
              </Text>
              <Text size="sm">
                : Displays a loading state using the loading prop. This is
                useful when data is being fetched or processed, ensuring that
                users are aware of the ongoing operation.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Custom Styles
              </Text>
              <Text size="sm">
                : Use the className prop to apply custom CSS classes for styling
                the Pagination component, enabling you to adapt the look and
                feel to your project's requirements.
              </Text>
            </li>
            <li>
              <Text size="sm" strong>
                Page Change Callback
              </Text>
              <Text size="sm">
                : The onPageChange callback provides a way to trigger actions
                whenever the user navigates between pages. It passes the new
                offset and limit, allowing you to update the data accordingly.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
            <ExpandableCard
              title=" Pagination"
              codeViewerProps={{
                code: PaginationExample1,
              }}
            >
              <Pagination
                limit={limit}
                offset={offset}
                total={total}
                siblingCount={siblingCount}
                onPageChange={handlePageChange}
              />
            </ExpandableCard>
            <ExpandableCard
              title=" Pagination"
              codeViewerProps={{
                code: PaginationExample2,
              }}
            >
              <Pagination
                limit={5}
                offset={0}
                total={100}
                siblingCount={2}
                onPageChange={handlePageChange}
                loading={false}
              />
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* limit */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">limit</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Number of items per page</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">10</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* offset */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">offset</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The current offset (starting index for the page)
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">0</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* total */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">total</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Total number of items in the pagination
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* siblingCount */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">siblingCount</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Number of sibling pages to display around the current
                        page
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">1</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* onPageChange */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onPageChange</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback triggered when the page changes
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Text size="xs" code>
                        {`(offset: number, limit: number) => void`}
                      </Typography.Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Displays loading state for the pagination
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const PaginationExample1 = `<Pagination
  limit={limit}
  offset={offset}
  total={total}
  siblingCount={siblingCount}
  onPageChange={handlePageChange}
/>`;

const PaginationExample2 = `<Pagination
  limit={5}
  offset={0}
  total={100}
  siblingCount={2}
  onPageChange={handlePageChange}
  loading={false}
/>`;

export default PaginationPage;
